import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from "./../app/apiClient.js";
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Badge from '@mui/material/Badge';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import UpdateIcon from '@mui/icons-material/Update';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { annotationsApi } from '../app/annotations.js';
import { convertViewSpecToTemplate } from '../app/viewSpecToTemplate.js';
import { checkViewValidity } from '../app/ivone_utils.js';
import ViewSelectorModal from '../Components/ViewSelectorModal.jsx';
import SymbologySelectorModal from '../Components/SymbologySelectorModal.jsx';

function ViewEditorPage(props) {
  const [searchParams] = useSearchParams();
  const viewIdFromUrl = searchParams.get("viewId");
  const inspectionIdFromUrl = searchParams.get("inspectionId");
  const [selectedViewId, setSelectedViewId] = useState(viewIdFromUrl || ""); // Currently selected view ID
  const [hasAnnotations, setHasAnnotations] = useState(false); // Check if annotations exist for this view
  const [viewSelectorModalOpen, setViewSelectorModalOpen] = useState(false); // Control view selector modal
  const [fields, setFields] = useState([]); // The template state (should be saved and filled on opening)
  const [formValues, setFormValues] = useState({}); // Things useful in the page but that should be present in the template
  const [newOptionValues, setNewOptionValues] = useState({}); // Store new option input values by field name
  const [draggedFieldIndex, setDraggedFieldIndex] = useState(null); // Track which field is being dragged
  const dropProcessedRef = useRef(false); // Prevent multiple drops
  const [newFieldTitle, setNewFieldTitle] = useState(''); // Store new field title input value
  const [dependencyTreeDrawerOpen, setDependencyTreeDrawerOpen] = useState(false); // Control dependency tree drawer
  const [showOptionsInDependencyTree, setShowOptionsInDependencyTree] = useState(false); // Control options display in dependency tree
  const [colorInputMode, setColorInputMode] = useState({}); // Track color input mode (text or picker) for each field: { "field_name": "text" | "picker" }
  const [colorTextValues, setColorTextValues] = useState({}); // Store text input values for colors: { "field_option": "R, G, B" }
  const [fillPatternFiles, setFillPatternFiles] = useState([]); // List of fill pattern PNG files from S3
  const [symbolFiles, setSymbolFiles] = useState([]); // List of symbol PNG files from S3
  const [hatchModalOpen, setHatchModalOpen] = useState(false); // Control hatch selector modal
  const [symbolModalOpen, setSymbolModalOpen] = useState(false); // Control symbol selector modal
  const [currentHatchField, setCurrentHatchField] = useState(null); // Store field name and option text for hatch selection: { fieldName, optionText }
  const [currentSymbolField, setCurrentSymbolField] = useState(null); // Store field name and option text for symbol selection: { fieldName, optionText }
  const viewLoadedRef = useRef(false); // Track if view has been loaded to prevent multiple loads
  const [viewImportLoading, setViewImportLoading] = useState(
    () => Boolean(viewIdFromUrl || inspectionIdFromUrl)
  );

  // Shared style constants
  const cellPadding = { padding: '8px' };
  const smallCellPadding = { padding: '4px' };
  const colorPickerStyle = { width: '40px', height: '40px', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'transparent', padding: 0 };

  // Load symbology files from S3 on component mount
  // NOTE: Temporarily disabled - hatches and symbols are not available for the moment
  useEffect(() => {
    // Don't load files for now - just set empty arrays
    setFillPatternFiles([]);
    setSymbolFiles([]);
    
    // Uncomment when ready to load files:
    // annotationsApi.listSymbologyFiles('fill_pattern', (files) => {
    //   const fileNames = files.map(file => file.replace(/\.png$/i, ''));
    //   setFillPatternFiles(fileNames);
    // });
    // annotationsApi.listSymbologyFiles('symbols', (files) => {
    //   const fileNames = files.map(file => file.replace(/\.png$/i, ''));
    //   setSymbolFiles(fileNames);
    // });
  }, []);

  // Keyboard shortcuts: T to toggle dependency tree, Escape to close
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Escape key to close dependency tree
      if (e.key === 'Escape' && dependencyTreeDrawerOpen) {
        e.preventDefault();
        setDependencyTreeDrawerOpen(false);
      } else if ((e.key === 't' || e.key === 'T') && 
          // Ignore if user is typing in an input field (INPUT, TEXTAREA, or contentEditable)
          e.target.tagName !== 'INPUT' && 
          e.target.tagName !== 'TEXTAREA' && 
          !e.target.isContentEditable) {
        e.preventDefault();
        setDependencyTreeDrawerOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [dependencyTreeDrawerOpen]);

  const make_id_from_text = (text) => {
    const raw = (text == null ? "" : String(text)).trim();
    return raw.normalize("NFD").replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  const setFormValue = (key, value) => {
    setFormValues({
      ...formValues,
      ...{[key]: value}
    })
  }
  const getFormValue = (key, default_value) => {
    return formValues[key] ? formValues[key] : default_value;
  }

  // Check if annotations exist for the view/inspection
  useEffect(() => {
    if (viewIdFromUrl && inspectionIdFromUrl) {
      annotationsApi.checkAnnotationsExist({ viewId: viewIdFromUrl, inspectionId: inspectionIdFromUrl }, (hasAnnotations) => {
        setHasAnnotations(hasAnnotations);
      });
    }
  }, [viewIdFromUrl, inspectionIdFromUrl]);

  const resolveOriginalInspectionView = useCallback((views) => {
    if (!views?.length) return null;
    if (inspectionIdFromUrl) {
      const inspectionViews = views.filter((v) => v.inspection?.id === inspectionIdFromUrl);
      if (inspectionViews.length > 0) {
        return inspectionViews[0];
      }
    }
    if (viewIdFromUrl) {
      return views.find((v) => v.id === viewIdFromUrl) ?? null;
    }
    return null;
  }, [inspectionIdFromUrl, viewIdFromUrl]);

  // Import view as template (manual action or autoload on page open)
  const importViewAsTemplate = useCallback((view) => {
    if (!view || !view.id) {
      alert("No view provided.");
      return;
    }

    setViewImportLoading(true);
    apiClient.req_as_standard(`query {view(id:"${view.id}"){id, spec}}`) // Query view
    .then((req_resp) => {
      const result = req_resp.data; // Get the graphql data response, ignoring errors
      const keys = result?.view?.spec?.description?.keys;
      if (!keys) {
        setViewImportLoading(false);
        alert("Failed to load view. Please try again.");
        return;
      }

      // Defer heavy template build so the loading overlay can paint first
      window.setTimeout(() => {
        try {
          const allFields = convertViewSpecToTemplate(keys);
          setFields(allFields);
          viewLoadedRef.current = true;
        } finally {
          setViewImportLoading(false);
        }
      }, 0);
    });
  }, []);

  // On open: load template from the view in URL, or the inspection's original view
  useEffect(() => {
    if (viewLoadedRef.current || (!inspectionIdFromUrl && !viewIdFromUrl)) {
      return;
    }

    if (viewIdFromUrl) {
      importViewAsTemplate({ id: viewIdFromUrl });
      return;
    }

    setViewImportLoading(true);
    annotationsApi.availableViews((views) => {
      const originalView = resolveOriginalInspectionView(views);
      if (originalView) {
        importViewAsTemplate(originalView);
      } else {
        setViewImportLoading(false);
      }
    });
  }, [inspectionIdFromUrl, viewIdFromUrl, resolveOriginalInspectionView, importViewAsTemplate]);

  // Helper function to clean spec (remove null/undefined values and fix NaN)
  const cleanSpec = (obj) => {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (Array.isArray(obj)) {
      return obj.map(cleanSpec);
    }
    if (typeof obj === 'object') {
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value === undefined || value === null) {
          continue; // Skip undefined and null values (GraphQL doesn't accept null for non-nullable types)
        }
        if (typeof value === 'number' && isNaN(value)) {
          continue; // Skip NaN values
        }
        cleaned[key] = cleanSpec(value);
      }
      return cleaned;
    }
    return obj;
  };

  // Update view on API
  const updateView = async () => {
    if (!selectedViewId) {
      alert("No view selected. Please select a view first.");
      return;
    }
    
    // Check if there are any fields
    if (fields.length === 0) {
      alert("Cannot update view: at least one field is required.");
      return;
    }
    
    // Check if the spec has any keys
    const spec = make_spec();
    if (!spec.keys || spec.keys.length === 0) {
      alert("Cannot update view: at least one key is required.");
      return;
    }
    
    // Validate the view spec before sending to API
    const validationResult = checkViewValidity(spec.keys);
    // Handle case where keys is not an array (returns array directly)
    if (Array.isArray(validationResult)) {
      alert(`View validation failed:\n${validationResult.join('\n')}`);
      return;
    }
    // Handle normal validation result with errors and warnings
    if (validationResult.errors && validationResult.errors.length > 0) {
      const errorMessages = validationResult.errors.join('\n');
      const warningMessages = validationResult.warnings && validationResult.warnings.length > 0 
        ? '\n\nWarnings:\n' + validationResult.warnings.join('\n')
        : '';
      alert(`View validation failed:\n${errorMessages}${warningMessages}`);
      return;
    }
    if (validationResult.warnings && validationResult.warnings.length > 0) {
      const warningMessages = validationResult.warnings.join('\n');
      if (!window.confirm(`View validation warnings:\n${warningMessages}\n\nDo you want to continue anyway?`)) {
        return;
      }
    }
    
    // Confirmation before update
    if (!window.confirm("Are you sure you want to update this view? This action cannot be undone.")) {
      return;
    }
    
    try {
      const rawSpec = {
        description: spec,
        sidebarConfig: {
          categoryKey: "category",
          levelKey: "", // Empty string for required String! type
          titleKey: "title"
        }
      };
      
      // Clean the spec to remove problematic values
      const cleanedSpec = cleanSpec(rawSpec);
      await annotationsApi.editView({ viewId: selectedViewId, spec: cleanedSpec });
      alert("View updated successfully!");
    } catch (error) {
      // Error is already thrown by annotationsApi.editView if result.data.errors exists
      const errorMessage = error.message || "Unknown error";
      alert(`Error updating view: ${errorMessage}`);
    }
  };

  // Save template as file
  const save_template_file = () => {
    // Prepare the file
    const textContent = JSON.stringify({
      "fields": fields
    });
    const fileName = (new Date()).toISOString() + ".json";
    const blob = new Blob([textContent], { type: "application/json" });
    // Make the browser download the file
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href); // Clean up the URL object
  }

  // Open template from file
  const open_template_file = () => {
    if (window.confirm("Are you sure to open an existing file? Your modifications on this page will be lost")){
      //const fileInput = document.getElementById('fileInput');
      //fileInput.click();

      const file_input = document.createElement("input");
      file_input.setAttribute("type", "file");
      file_input.addEventListener("change", (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (e) => {
          const content = JSON.parse(e.target.result);
          // Parse it
          setFields(content.fields)
          document.body.removeChild(file_input);
        };

        reader.onerror = () => {
          alert("Error reading file");
          document.body.removeChild(file_input);
        };

        reader.readAsText(file); // You can change this if you want other formats
      })
      document.body.appendChild(file_input);
      file_input.click();
    }
  }

  // Add a new key in the view spec: select, number, or textarea)
  const addField = () => {
    const title = newFieldTitle.trim();
    const type = getFormValue("new_field_type", "select");
    const newField = {
      name: make_id_from_text(title),
      title: title,
      type: type,
      depends_on: "",
      // UI convention: "-" represents an empty value for select defaults.
      // Persisted backend value remains "" (see save conversion below) so older views/history keep working.
      default: type === "select" ? "-" : ""
    };
    if (type === "select") {
      // Don't create options automatically - user must add them manually
      newField.options = [];
    } else if (type === "number") {
      newField.min = "0";
      newField.max = "0";
    }
    setFields([...fields, newField]);
    setNewFieldTitle('');
    setFormValue("new_field_type", "select");
  }

  // Add a new option to a select key
  const addOption = (field_name) => {
    const val = (newOptionValues[field_name] || '').trim();
    const field = fields.find(f => f.name === field_name);
    if (!val) {
      alert("Error! Option name cannot be empty!");
      return;
    }
    // Check for duplicates: "-" and "" are considered the same (both represent empty value)
    // Also check that we don't have duplicate text values
    const hasDuplicate = field?.options.some(opt => {
      // Same text value
      if (opt.text === val) return true;
      // "-" and "" are considered duplicates (both represent empty)
      if ((opt.text === "-" || opt.text === "") && (val === "-" || val === "")) return true;
      return false;
    });
    if (hasDuplicate) {
      alert("Error! there is already an option with this name!");
      return;
    }
    // Store "-" as text for display, but it represents empty value internally
    const optionText = val === "-" ? "-" : val;
    setFields(currentFields => currentFields.map(f => 
      f.name === field_name 
        ? {...f, options: [...f.options, {text: optionText, parent_options: {}}]}
        : f
    ));
    setNewOptionValues({...newOptionValues, [field_name]: ''});
  }

  // Remove a key from the view spec
  const removeField = (field_name) => {
    // Use functional update to ensure we have the latest state
    setFields((currentFields) => {
      // Find all child fields that depend on this field
      const childFieldsToRemove = currentFields
        .filter((field) => field.depends_on === field_name)
        .map((field) => field.name);
      
      // Remove the field and all its child fields
      const fieldsToRemove = [field_name, ...childFieldsToRemove];
      const updatedFields = currentFields.filter((field) => !fieldsToRemove.includes(field.name));
      
      // Clean up new option values for removed fields (only this is needed for input state)
      const newOptionVals = { ...newOptionValues };
      fieldsToRemove.forEach((name) => {
        delete newOptionVals[name];
      });
      setNewOptionValues(newOptionVals);
      
      return updatedFields;
    });
  }

  const handleDragStart = (e, index) => {
    // Don't start drag if clicking on an input or select
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || 
        e.target.closest('input') || e.target.closest('select') ||
        e.target.closest('.MuiInputBase-root') || e.target.closest('.MuiSelect-root')) {
      e.preventDefault();
      return;
    }
    setDraggedFieldIndex(index);
    dropProcessedRef.current = false;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropProcessedRef.current || draggedFieldIndex === null || draggedFieldIndex === dropIndex) {
      setDraggedFieldIndex(null);
      dropProcessedRef.current = false;
      return;
    }
    dropProcessedRef.current = true;
    const newFields = [...fields];
    const [draggedField] = newFields.splice(draggedFieldIndex, 1);
    // Insert at new position: if moving down (draggedIndex < dropIndex), adjust by -1 
    // because the element was already removed, shifting indices. If moving up, use dropIndex directly.
    newFields.splice(draggedFieldIndex < dropIndex ? dropIndex - 1 : dropIndex, 0, draggedField);
    setFields(newFields);
    setDraggedFieldIndex(null);
  }

  // Reset drag state
  const handleDragEnd = () => {
    setDraggedFieldIndex(null);
    dropProcessedRef.current = false;
  }

  // Scroll to an element by ID (used for anchors in the dependency tree)
  const scrollToElement = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Build dependency tree structure from fields
  const dependencyTree = useMemo(() => {
    const buildTree = (parentName = null) => {
      return fields
        .filter(field => {
          if (parentName === null) {
            return !field.depends_on;
          }
          return field.depends_on === parentName;
        })
        .map(field => ({
          field,
          children: buildTree(field.name)
        }));
    };
    return buildTree();
  }, [fields]);

  // Recursive function to render tree with options and dependencies
  const renderTree = useCallback((nodes, level = 0, prefix = '') => {
    return nodes.map((node, idx) => {
      const isLast = idx === nodes.length - 1;
      const connector = isLast ? '└── ' : '├── ';
      const currentPrefix = prefix + connector;
      const childPrefix = prefix + (isLast ? '    ' : '│   ');
      const indentAmount = showOptionsInDependencyTree ? (level * 20) : (level * 28);
      
      return (
        <React.Fragment key={node.field.name}>
          {/* KEY (Field) */}
          <Box 
            sx={{ mb: 0.75, cursor: 'pointer', p: '4px 8px', ml: `${indentAmount}px`, borderRadius: 1, transition: 'bgcolor 0.2s', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } }}
            onClick={() => scrollToElement(`field_${node.field.name}`)}
          >
            <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: '600', fontSize: '0.95rem' }}>
              {node.field.title} <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 'normal' }}>[{node.field.type}]</span>
            </Typography>
          </Box>
          
          {/* Display options if it's a select field and showOptionsInDependencyTree is enabled */}
          {node.field.type === 'select' && node.field.options && node.field.options.length > 0 && showOptionsInDependencyTree ? (
            <Box sx={{ ml: `${indentAmount + 16}px`, mb: 1 }}>
              {node.field.options.map((option, optIdx) => {
                const dependentChildren = node.children.filter(childNode => {
                  return childNode.field.options?.some(childOpt => 
                    childOpt.parent_options?.[option.text] === true
                  );
                });
                
                const parentColor = option.symbology?.["[]"]?.color;
                const parentColorString = parentColor ? `rgb(${parentColor[0]}, ${parentColor[1]}, ${parentColor[2]})` : null;
                
                return (
                  <Box key={optIdx} sx={{ mb: 1 }}>
                    {/* OPTION */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pl: 1 }}>
                      {parentColorString && (
                        <Box sx={{ bgcolor: parentColorString, borderColor: 'rgba(255, 255, 255, 0.3)', flexShrink: 0 }} />
                      )}
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                        • {option.text}
                      </Typography>
                    </Box>
                    
                    {/* Child fields and their dependent options */}
                    {dependentChildren.length > 0 && dependentChildren.map((childNode) => {
                      const dependentOptions = childNode.field.options?.filter(childOpt =>
                        childOpt.parent_options?.[option.text] === true
                      ) || [];
                      
                      if (dependentOptions.length === 0) return null;
                      
                      return (
                        <Box key={childNode.field.name} sx={{ ml: 2, mb: 1 }}>
                          {/* Child field */}
                          <Box
                            sx={{ cursor: 'pointer', p: '2px 6px', mb: 0.5, borderRadius: 1, transition: 'bgcolor 0.2s', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
                            onClick={() => scrollToElement(`field_${childNode.field.name}`)}
                          >
                            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: '500', fontSize: '0.85rem' }}>
                              {childNode.field.title} <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 'normal' }}>[{childNode.field.type}]</span>
                            </Typography>
                          </Box>
                          
                          {/* Dependent options */}
                          <Box sx={{ ml: 2 }}>
                            {dependentOptions.map((depOpt, depOptIdx) => {
                              const depOptColor = depOpt.symbology?.["[]"]?.color;
                              const depOptColorString = depOptColor 
                                ? `rgb(${depOptColor[0]}, ${depOptColor[1]}, ${depOptColor[2]})`
                                : parentColorString;
                              
                              return (
                                <Box key={depOptIdx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, pl: 1 }}>
                                  {depOptColorString && (
                                    <Box sx={{ width: 14, height: 14, bgcolor: depOptColorString, border: '1px solid', borderColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '3px', flexShrink: 0 }} />
                                  )}
                                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' }}>
                                    • {depOpt.text}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          ) : (
            node.children.length > 0 && renderTree(node.children, level + 1, childPrefix)
          )}
        </React.Fragment>
      );
    });
  }, [showOptionsInDependencyTree, scrollToElement]);

  const removeOption = (field_name, option_text) => {
    const new_fields = fields.map((field) => {
      if (field.name === field_name) {
        return {
          ...field,
          options: field.options.filter((opt) => opt.text !== option_text)
        };
      }
      return field;
    });
    setFields(new_fields);
  }

  const updateField = (field_name, new_field_elements) => {
    setFields(currentFields => currentFields.map(f => f.name === field_name ? {...f, ...new_field_elements} : f));
  }

  const updateOption = (field_name, option_id, new_field_elements) => {
    setFields(currentFields => currentFields.map(field => 
      field.name === field_name 
        ? {...field, options: field.options.map(opt => 
            opt.text === option_id ? {...opt, ...new_field_elements} : opt
          )}
        : field
    ));
  }

  const setDependency = (field_name, child_option_text, parent_option_text, valid) => {
    setFields(currentFields => currentFields.map(field => 
      field.name === field_name 
        ? {...field, options: field.options.map(opt => 
            opt.text === child_option_text 
              ? {...opt, parent_options: {...opt.parent_options, [parent_option_text]: valid}}
              : opt
          )}
        : field
    ));
  }

  // Update the color of an option
  const updateOptionColor = (field_name, option_text, color) => {
    const new_fields = fields.map((field) => {
      if (field.name === field_name) {
        return {
          ...field,
          options: field.options.map((opt) => {
            if (opt.text === option_text) {
              const updatedOpt = { ...opt };
              if (!updatedOpt.symbology) updatedOpt.symbology = {};
              if (!updatedOpt.symbology["[]"]) updatedOpt.symbology["[]"] = {};
              if (color === null) {
                updatedOpt.symbology["[]"].color = null;
              } else {
                updatedOpt.symbology["[]"].color = color;
              }
              return updatedOpt;
            }
            return opt;
          })
        };
      }
      return field;
    });
    setFields(new_fields);
  }

  // Update the hatch of an option
  const updateOptionHatch = (field_name, option_text, hatch) => {
    const new_fields = fields.map((field) => {
      if (field.name === field_name) {
        return {
          ...field,
          options: field.options.map((opt) => {
            if (opt.text === option_text) {
              const updatedOpt = { ...opt };
              if (!updatedOpt.symbology) updatedOpt.symbology = {};
              if (!updatedOpt.symbology["[]"]) updatedOpt.symbology["[]"] = {};
              updatedOpt.symbology["[]"].fill_pattern = hatch || null;
              return updatedOpt;
            }
            return opt;
          })
        };
      }
      return field;
    });
    setFields(new_fields);
  }

  // Update the symbol of an option
  const updateOptionSymbol = (field_name, option_text, symbol) => {
    const new_fields = fields.map((field) => {
      if (field.name === field_name) {
        return {
          ...field,
          options: field.options.map((opt) => {
            if (opt.text === option_text) {
              const updatedOpt = { ...opt };
              if (!updatedOpt.symbology) updatedOpt.symbology = {};
              if (!updatedOpt.symbology["[]"]) updatedOpt.symbology["[]"] = {};
              updatedOpt.symbology["[]"].symbol = symbol || null;
              return updatedOpt;
            }
            return opt;
          })
        };
      }
      return field;
    });
    setFields(new_fields);
  }

  // Generate a view JSON
  const make_spec = () => {
    let keys = [];
    
    // Group child fields by originalName (or name if no originalName) to avoid processing duplicates
    // Fields with same originalName but different names (from import) should be merged
    const processedChildFields = new Set(); // Track which child field groups we've processed

    // For each field (field to be seen as a form input for the user)
    for (const field of fields){

      // NUMBER
      if (field.type === "number"){
        // GraphQL Int type is 32-bit signed integer: range is -2,147,483,648 to 2,147,483,647
        const INT32_MAX = 2147483647;
        const INT32_MIN = -2147483648;
        
        const minValue = parseFloat(field.min);
        const maxValue = parseFloat(field.max);
        
        // Clamp values to valid Int32 range
        const clampedMin = isNaN(minValue) ? 0 : Math.max(INT32_MIN, Math.min(INT32_MAX, minValue));
        const clampedMax = isNaN(maxValue) ? 0 : Math.max(INT32_MIN, Math.min(INT32_MAX, maxValue));
        
        keys.push({
          "type": "number",
          "name": field.name,
          "title": field.title,
          "default": field.default || null,
          "max": clampedMax,
          "min": clampedMin
        });
      }
      // TEXTAREA
      else if (field.type === "textarea"){
        keys.push({
          "type": "textarea",
          "name": field.name,
          "title": field.title,
          "default": field.default
        });
      }
      // SELECT
      else if (field.type === "select"){
        // If this is a unique or parent field, not a submenu
        if (field.depends_on === ""){
          let key = {
            "name": field.name,
            "title": field.title,
            "type": field.type
          }
          // Check if there are child fields (submenus) for this parent field
          // Find all child fields that depend on this parent field (by name)
            const child_fields = fields.filter((candidate_field) => {
              if (candidate_field.name === field.name) return false; // Discard the current field
            // Check if candidate depends on this field
            if (candidate_field.depends_on === field.name) return true;
              else return false;
            })
          
          // Group child fields by name to get unique child field names for submenus
          // (fields are already grouped by name during import)
          const uniqueChildFieldNames = new Set();
          child_fields.forEach((child_field) => {
            uniqueChildFieldNames.add(child_field.name);
          })
          
          key.options = field.options.map((opt) => {
            // DB rule (new): the empty option is stored as "-" (not "").
            // Backward compat: if a legacy template still has "", upgrade it to "-" on save.
            const optText = opt.text === "" ? "-" : opt.text;
            // ID rule: parent option ids must never be empty, but submenu option ids may use a trailing "-"
            // (e.g. "divers-") to represent the empty choice.
            const rawForId = optText === "-" ? "" : optText;
            const baseId = make_id_from_text(rawForId);
            const opt_id = baseId || "empty";
            // Make the option
            let option = {
              "id": opt_id,
              "text": optText
            }
            // Add color if present in symbology
            if (opt.symbology?.["[]"]?.color) {
              option.color = opt.symbology["[]"].color; // Color as [r, g, b] array
            }
            // Add submenus if this option has child fields
            // submenus format: array of IDs like "opt_id-childFieldName"
            // Use unique child field names to avoid duplicates
            if (uniqueChildFieldNames.size > 0) {
              option.submenus = Array.from(uniqueChildFieldNames).map((childFieldName) => {
                // Format: "parentOptionId-childFieldName"
                return opt_id + "-" + childFieldName;
              });
            }
            return option;
          })
          // Use field.default if defined (should be option text), otherwise use first option, or null if no options
          // Handle empty string and "-" as valid default values (both represent empty)
          if (field.default !== undefined && field.default !== null && field.default !== "") {
            const defaultText = field.default === "" ? "-" : field.default;
            const defaultOpt =
              key.options.find(opt => opt.text === defaultText) ??
              (defaultText === "-" ? key.options.find(opt => opt.text === "") : undefined);
            if (defaultOpt) {
              key.default = defaultOpt.id;
            } else {
          key.default = key.options.length > 0 ? key.options[0].id : null;
            }
          } else {
            // Handle empty string or "-" as default
            const emptyOpt = key.options.find(opt => opt.text === "-") ?? key.options.find(opt => opt.text === "");
            key.default = emptyOpt ? emptyOpt.id : (key.options.length > 0 ? key.options[0].id : null);
          }
          keys.push(key);
        }
        // If this is a childfield (a submenu)
        else{
          // Skip if we've already processed this child field (fields are already grouped by name during import)
          const childFieldGroupId = field.name + "_" + field.depends_on;
          if (processedChildFields.has(childFieldGroupId)) {
            continue;
          }
          processedChildFields.add(childFieldGroupId);
          
          // Find the parent key that was already created (parent keys are processed before child keys)
          const parent_key = keys.find((k) => k.name === field.depends_on && k.type === "select");
          
          // Skip if parent key not found or has no options
          if (!parent_key || !parent_key.options || parent_key.options.length === 0) {
            continue;
          }
          
          // Loop over all parent options and create a child key for each one (original logic)
            for (const parent_opt of parent_key.options){
              const prefix = parent_opt.id + "-";
            // Convert parent_opt.text from backend format ("") to frontend format ("-") for matching
            const parentOptTextForMatching = parent_opt.text === "" ? "-" : parent_opt.text;
            
            // Filter options that belong to this parent option
            const child_options = field.options
              .filter((child_opt) => {
                // Match using the frontend format (where "" is displayed as "-")
                return child_opt.parent_options[parentOptTextForMatching] === true;
              })
              .map((opt) => {
                // DB rule (new): the empty option is stored as "-" (not "").
                // Backward compat: if a legacy template still has "", upgrade it to "-" on save.
                const optText = opt.text === "" ? "-" : opt.text;
                // For submenu options, keep legacy empty-id shape: prefix + "" => "divers-"
                const rawForId = optText === "-" ? "" : optText;
                const baseId = make_id_from_text(rawForId);
                const opt_id = prefix + (baseId || "");
                // Make the option
                let option = {
                  "id": opt_id,
                  "text": optText
                }
                // Add color if present in symbology (check the "[]" key for the simplified structure)
                if (opt.symbology?.["[]"]?.color) {
                  option.color = opt.symbology["[]"].color; // Color as [r, g, b] array
                }
                // Check if this child field has its own child fields (sub-submenus)
                const childFieldName = field.name;
                const child_fields = fields.filter((candidate_field) => {
                  if (candidate_field.name === childFieldName) return false;
                  else if (candidate_field.depends_on === childFieldName) return true;
                  else return false;
                });
                if (child_fields.length > 0) {
                  // Group by name to get unique names (fields are already grouped by name during import)
                  const uniqueChildFieldNames = new Set();
                  child_fields.forEach((child_field) => {
                    uniqueChildFieldNames.add(child_field.name);
                  });
                  option.submenus = Array.from(uniqueChildFieldNames).map((childFieldName) => {
                    // Format: "childOptionId-grandChildFieldName"
                    return opt_id + "-" + childFieldName;
                  });
                }
                return option;
              });
            
            // Create a child key for this parent option if there are options
            if (child_options.length > 0) {
              let key = {
                "id": prefix + field.name, // Explicit ID: "parentOptionId-childFieldName"
                "name": field.name, // Use the field name (already grouped during import)
                "title": field.title,
                "type": field.type
              };
              key.options = child_options;
              // Determine default value for this specific child key
              if (field.default !== undefined && field.default !== null && field.default !== "") {
                const defaultText = field.default === "" ? "-" : field.default;
                const defaultOpt =
                  key.options.find(opt => opt.text === defaultText) ??
                  (defaultText === "-" ? key.options.find(opt => opt.text === "") : undefined);
                if (defaultOpt) {
                  key.default = defaultOpt.id;
                } else {
                  // Try to find an option with "-" text as fallback
                  const emptyOpt = key.options.find(opt => opt.text === "-") ?? key.options.find(opt => opt.text === "");
                  key.default = emptyOpt ? emptyOpt.id : (key.options.length > 0 ? key.options[0].id : null);
                }
              } else {
                // Handle empty string or "-" as default
                const emptyOpt = key.options.find(opt => opt.text === "-") ?? key.options.find(opt => opt.text === "");
                key.default = emptyOpt ? emptyOpt.id : (key.options.length > 0 ? key.options[0].id : null);
              }
              keys.push(key);
            }
          }
        }
      }
      else {
        return {"error": "unknown field " + field.type}
      }
    }
    return {"keys": keys}
  }

  const renderField = (field, field_index) => {
    if (field.type === "select") return renderSelectField(field, field_index);
    if (field.type === "number") return renderNumberField(field, field_index);
    if (field.type === "textarea") return renderTextAreaField(field, field_index);
    return <h1>Error</h1>;
  }


  const FieldHeader = ({field, field_index, onDelete}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <TextField 
        label={`Field #${field_index+1} [${field.type}]`}
        value={field.title} 
        onChange={(e) => updateField(field.name, {title: e.target.value})}
        sx={{ flex: 1 }}
      />
      <IconButton color="error" onClick={onDelete} title="Delete field">
        <DeleteIcon />
      </IconButton>
    </Box>
  );

  const renderNumberField = (field, field_index) => {
    // GraphQL Int type is 32-bit signed integer: range is -2,147,483,648 to 2,147,483,647
    const INT32_MAX = 2147483647;
    const INT32_MIN = -2147483648;
    
    const validateAndClampNumber = (value, fieldName) => {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value; // Keep as-is if not a number
      
      if (numValue > INT32_MAX) {
        alert(`Warning: Value ${numValue} exceeds maximum Int32 value (${INT32_MAX}). It will be clamped to ${INT32_MAX}.`);
        return INT32_MAX.toString();
      }
      if (numValue < INT32_MIN) {
        alert(`Warning: Value ${numValue} is below minimum Int32 value (${INT32_MIN}). It will be clamped to ${INT32_MIN}.`);
        return INT32_MIN.toString();
      }
      return value;
    };
    
    return (
      <Paper id={`field_${field.name}`} sx={{ p: 3 }}>
        <FieldHeader field={field} field_index={field_index} onDelete={() => removeField(field.name)} />
        <Stack direction="row" spacing={2}>
          <TextField 
            label="Min" 
            type="number" 
            value={field.min} 
            onChange={(e) => {
              const clamped = validateAndClampNumber(e.target.value, 'min');
              updateField(field.name, {min: clamped});
            }} 
            inputProps={{ min: INT32_MIN, max: INT32_MAX }}
            sx={{ flex: 1 }} 
          />
          <TextField 
            label="Max" 
            type="number" 
            value={field.max} 
            onChange={(e) => {
              const clamped = validateAndClampNumber(e.target.value, 'max');
              updateField(field.name, {max: clamped});
            }} 
            inputProps={{ min: INT32_MIN, max: INT32_MAX }}
            sx={{ flex: 1 }} 
          />
          <TextField 
            label="Default" 
            type="number" 
            value={field.default} 
            onChange={(e) => {
              const clamped = validateAndClampNumber(e.target.value, 'default');
              updateField(field.name, {default: clamped});
            }} 
            inputProps={{ min: INT32_MIN, max: INT32_MAX }}
            sx={{ flex: 1 }} 
          />
        </Stack>
      </Paper>
    );
  };

  const renderTextAreaField = (field, field_index) => (
    <Paper id={`field_${field.name}`} sx={{ p: 3 }}>
      <FieldHeader field={field} field_index={field_index} onDelete={() => removeField(field.name)} />
      <TextField label="Default" value={field.default} onChange={(e) => updateField(field.name, {default: e.target.value})} fullWidth />
    </Paper>
  );

  const renderSelectField = (field, field_index) => {
    const dependance_options = field.depends_on ? fields.find(f => f.name === field.depends_on)?.options || [] : [];
    return <Paper id={`field_${field.name}`} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
          <TextField 
            label={`Field #${field_index+1} [${field.type}]`}
            value={field.title} 
            onChange={(e) => updateField(field.name, {title: e.target.value})}
            sx={{ flex: 1 }}
          />
          <FormControl sx={{ flex: 1 }}>
            <InputLabel>Depends on</InputLabel>
            <Select value={field.depends_on} onChange={(e) => {
              const newDependsOn = e.target.value;
              setFields(currentFields => {
                let updatedFields = currentFields.map(f => f.name === field.name ? {...f, depends_on: newDependsOn} : f);
                if (newDependsOn && field.default !== undefined && field.default !== null) {
                  const parentField = updatedFields.find(f => f.name === newDependsOn);
                  const updatedField = updatedFields.find(f => f.name === field.name);
                  const defaultOption = updatedField?.options?.find(opt => opt.text === field.default);
                  if (parentField?.options && defaultOption) {
                    updatedFields = updatedFields.map(f => 
                      f.name === field.name 
                        ? {...f, options: f.options.map(opt => 
                            opt.text === defaultOption.text 
                              ? {...opt, parent_options: parentField.options.reduce((acc, parentOpt) => ({...acc, [parentOpt.text]: true}), {})}
                              : {...opt, parent_options: {}} // Clear parent_options for other options when depends_on changes
                          )}
                        : f
                    );
                  }
                } else if (!newDependsOn) {
                  // When removing depends_on, clear all parent_options
                  updatedFields = updatedFields.map(f => 
                    f.name === field.name 
                      ? {...f, options: f.options.map(opt => ({...opt, parent_options: {}}))}
                      : f
                  );
                }
                return updatedFields;
              });
            }} label="Depends on">
              <MenuItem value="">None</MenuItem>
              {fields.filter(f2 => f2.name !== field.name && f2.type === "select" && f2.depends_on === "").map(f2 => 
                <MenuItem key={f2.name} value={f2.name}>{f2.title}</MenuItem>
              )}
            </Select>
          </FormControl>
        </Stack>
        <IconButton color="error" onClick={() => removeField(field.name)} title="Delete field">
          <DeleteIcon />
        </IconButton>
      </Box>
      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel>Default value</InputLabel>
          <Select 
            value={field.default !== undefined && field.default !== null ? field.default : ''}
            onChange={(e) => {
              const newDefault = e.target.value;
              updateField(field.name, {default: newDefault});
              if (field.depends_on && newDefault !== undefined && newDefault !== null && dependance_options.length > 0) {
                const defaultOption = field.options.find(opt => opt.text === newDefault);
                if (defaultOption) {
                  dependance_options.forEach(parentOption => {
                    setDependency(field.name, defaultOption.text, parentOption.text, true);
                  });
                }
              }
            }}
            label="Default value"
          >
            <MenuItem value="">None</MenuItem>
            {field.options.map(opt => {
              const displayText = (opt.text === '' || opt.text === '-') ? '-' : opt.text;
              const keyValue = opt.text === '' ? `empty-${field.name}` : opt.text;
              return (
                <MenuItem key={keyValue} value={opt.text}>
                  {opt.text === '' || opt.text === '-' ? <span style={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.5)' }}>-</span> : opt.text}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 400 }} size="small">
          <TableHead>
            <TableRow>
                <TableCell sx={{ ...cellPadding, maxWidth: 100, minWidth: 60 }}>Options</TableCell>
                <TableCell 
                  sx={{ 
                    ...cellPadding,
                    cursor: 'pointer',
                    userSelect: 'none',
                    maxWidth: '60px',
                    minWidth: '50px',
                    ...((colorInputMode[field.name] || 'picker') === 'text' && { minWidth: '50px' }),
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                  }}
                  onClick={() => {
                    const currentMode = colorInputMode[field.name] || 'picker';
                    const newMode = currentMode === 'text' ? 'picker' : 'text';
                    setColorInputMode({ ...colorInputMode, [field.name]: newMode });
                    // Initialize text values for all options when switching to text mode
                    if (newMode === 'text') {
                      const newTextValues = { ...colorTextValues };
                      field.options.forEach(opt => {
                        const optColorKey = `${field.name}_${opt.text}`;
                        if (!newTextValues[optColorKey]) {
                          const optColor = opt.symbology?.["[]"]?.color;
                          const optColorString = optColor ? `(${optColor[0]}, ${optColor[1]}, ${optColor[2]})` : '';
                          newTextValues[optColorKey] = optColorString;
                        }
                      });
                      setColorTextValues(newTextValues);
                    }
                  }}
                  title="Click to toggle between text input and color picker"
                >
                  Color
                </TableCell>
                <TableCell sx={{ ...cellPadding, maxWidth: '70px', minWidth: '60px' }}>Hatch</TableCell>
                <TableCell sx={{ ...cellPadding, maxWidth: '70px', minWidth: '60px' }}>Symbol</TableCell>
              {dependance_options.map(opt => (
                <TableCell key={opt.text} sx={{ ...cellPadding, minWidth: 40, maxWidth: 50, textAlign: 'center', px: 0.5 }}>{opt.text}</TableCell>
              ))}
                <TableCell sx={{ ...cellPadding, minWidth: 50, maxWidth: 60, textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {field.options.map((option) => {
                const currentColor = option.symbology?.["[]"]?.color;
                const colorKey = `${field.name}_${option.text}`;
                const isTextMode = (colorInputMode[field.name] || 'picker') === 'text';
                const colorString = currentColor ? `(${currentColor[0]}, ${currentColor[1]}, ${currentColor[2]})` : '';
                const textValue = colorTextValues[colorKey] !== undefined ? colorTextValues[colorKey] : colorString;
                
              return (
                <TableRow key={(option.text === '' || option.text === '-') ? `empty-${field.name}` : option.text}>
                  <TableCell sx={{ ...cellPadding, maxWidth: 100, minWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(option.text === '' || option.text === '-') ? <span style={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.5)' }}>-</span> : option.text}
                  </TableCell>
                  <TableCell sx={{ ...cellPadding, maxWidth: '60px', minWidth: '50px', ...(isTextMode && { minWidth: '50px' }) }}>
                    {isTextMode ? (
                      <TextField
                        size="small"
                        placeholder="(R, G, B)"
                        value={textValue}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const value = e.target.value;
                          setColorTextValues({ ...colorTextValues, [colorKey]: value });
                          const regex = /\s*[\(]?\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*[\)]?/;
                          const match = value.match(regex);
                          if (match) {
                            const r = parseInt(match[1], 10);
                            const g = parseInt(match[2], 10);
                            const b = parseInt(match[3], 10);
                            if (r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255) {
                              updateOptionColor(field.name, option.text, [r, g, b]);
                            }
                          } else if (value.trim() === '') {
                            updateOptionColor(field.name, option.text, null);
                          }
                        }}
                        onBlur={() => {
                          // Reset to actual color value when blurring if input is invalid
                          if (textValue !== colorString) {
                            const regex = /\s*[\(]?\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*[\)]?/;
                            if (!textValue.match(regex) && textValue.trim() !== '') {
                              setColorTextValues({ ...colorTextValues, [colorKey]: colorString });
                            }
                          }
                        }}
                        sx={{ 
                          width: '100%',
                          maxWidth: '60px',
                          '& .MuiInputBase-input': {
                            bgcolor: currentColor ? `rgba(${currentColor[0]}, ${currentColor[1]}, ${currentColor[2]}, 0.8)` : 'transparent',
                            color: 'white',
                            fontSize: '0.75rem',
                            p: '4px 6px'
                          }
                        }}
                      />
                    ) : (
                      <input
                        type="color"
                        value={currentColor ? `#${currentColor[0].toString(16).padStart(2, '0')}${currentColor[1].toString(16).padStart(2, '0')}${currentColor[2].toString(16).padStart(2, '0')}` : '#000000'}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const hex = e.target.value;
                          // Convert hex to RGB array [r, g, b]
                          const r = parseInt(hex.slice(1, 3), 16);
                          const g = parseInt(hex.slice(3, 5), 16);
                          const b = parseInt(hex.slice(5, 7), 16);
                          // Update color - stored as [r, g, b] array in symbology["[]"].color
                          updateOptionColor(field.name, option.text, [r, g, b]);
                          // Update text value if in text mode
                          if (isTextMode) {
                            const rgbString = `(${r}, ${g}, ${b})`;
                            setColorTextValues({ ...colorTextValues, [colorKey]: rgbString });
                          }
                        }}
                        style={colorPickerStyle}
                        title="Pick a color"
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...cellPadding, maxWidth: '70px', minWidth: '60px', px: 0.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setCurrentHatchField({ fieldName: field.name, optionText: option.text });
                        setHatchModalOpen(true);
                      }}
                      sx={{ fontSize: '0.75rem', minWidth: 50, maxWidth: 60, width: '100%', px: 0.5 }}
                    >
                      {option.symbology?.["[]"]?.fill_pattern || 'None'}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ ...cellPadding, maxWidth: '70px', minWidth: '60px', px: 0.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setCurrentSymbolField({ fieldName: field.name, optionText: option.text });
                        setSymbolModalOpen(true);
                      }}
                      sx={{ fontSize: '0.75rem', minWidth: 50, maxWidth: 60, width: '100%', px: 0.5 }}
                    >
                      {option.symbology?.["[]"]?.symbol || 'None'}
                    </Button>
                  </TableCell>
                {dependance_options.map(opt => (
                  <TableCell key={opt.text} sx={{ ...smallCellPadding, textAlign: 'center', px: 0.5, minWidth: 40, maxWidth: 50 }}>
                    <Checkbox 
                      checked={option.parent_options[opt.text] || false} 
                      onChange={(e) => setDependency(field.name, option.text, opt.text, e.target.checked)}
                      size="small"
                    />
                  </TableCell>
                ))}
                  <TableCell sx={{ ...cellPadding, textAlign: 'center', minWidth: 50, maxWidth: 60, px: 0.5 }}>
                    <IconButton 
                      color="error" 
                      size="small"
                      onClick={() => removeOption(field.name, option.text)}
                      title="Delete option"
                      sx={{ padding: '4px' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
        <Stack direction="row" spacing={2}>
          <TextField 
            className="new_option_name"
            label="New option name"
            value={newOptionValues[field.name] || ''}
            onChange={(e) => setNewOptionValues({ ...newOptionValues, [field.name]: e.target.value })}
            fullWidth
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addOption(field.name);
              }
            }}
          />
          <Button variant="contained" onClick={() => addOption(field.name)}>
            Add an option
          </Button>
        </Stack>
      </Stack>
    </Paper>
  }

  const make_tree_list = (condition, fields) => {
    let list = [];
    if (fields.length > 0){
      const field = fields[0];
      for (let opt of field.options){
        // If valid:
        if (condition == null || opt.parent_options[condition] === true){
          if (fields.length > 1){
            let subresult = make_tree_list(opt.text, fields.slice(1));
            for (let subelt of subresult){
              list.push([opt.text].concat(subelt));
            }
          }
          else{
            list.push([opt.text]);
          }
        }
      }
    }
    return list;
  }

  // renderSymbology
  const renderSymbology = () => {
    // Child and parents
    const selects = fields.filter((field) => field.type === "select");
    let child_to_parent = {};
    for (let field of selects){
      if (field.depends_on){
        child_to_parent[field.name] = field.depends_on;
      }
    }
    let parent_to_child = {};
    for (let child of Object.keys(child_to_parent)){
      parent_to_child[child_to_parent[child]] = child;
    }

    // 
    let trees = [];
    for (let field of selects){
      // Compute ancesters
      let ancesters = [field]
      if (child_to_parent[field.name]){
        // safer while true
        let current_name = field.name;
        for (let i=0; i<selects.length; i++){
          current_name = child_to_parent[current_name];
          if (current_name == null) break;
          else ancesters.unshift(selects.filter((el) => el.name === current_name)[0]);
        }
      }
      // Make tablebody
      let tablebody = [];
      for (let list_elt of make_tree_list(null, ancesters)){
        // Find value
        const field_name = ancesters[ancesters.length-1].name;
        let opt = ancesters[ancesters.length-1].options.find((opt) => opt.text === list_elt[list_elt.length-1]);
        const option_id = opt.text;
        let rule_condition = JSON.stringify(list_elt.slice(0, -1));
        let symbology = JSON.parse(JSON.stringify(opt.symbology ? opt.symbology : {}))

        // Text field
        const textField = <TextField onChange={(e) => {
          // Find RGB value 
          const regex = /\s*[\(]?\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*[\)]?/;
          const match = e.target.value.match(regex);
          // If RGB is detected, let's save the color
          if (match){
            const r = parseInt(match[1]);
            const g = parseInt(match[2]);
            const b = parseInt(match[3]);
            if (symbology[rule_condition] == null) symbology[rule_condition] = {};
            symbology[rule_condition].color = [r, g, b];
            updateOption(field_name, option_id, {"symbology": symbology});
          }
          // Otherwise, disable color
          else{
            if (symbology[rule_condition] == null) symbology[rule_condition] = {};
            symbology[rule_condition].color = null;
            updateOption(field_name, option_id, {"symbology": symbology});
          }
        }} label="Color (R, G, B). Example: (255, 0, 0)" value={symbology[rule_condition]?.color} variant="outlined" />;

        // Add table row
        tablebody.push(<TableRow>
          {list_elt.map((title) => <TableCell>{title}</TableCell>)}
          <TableCell>
            {symbology[rule_condition]?.color && <Badge style={{"background-color": `rgb(${symbology[rule_condition]?.color[0]}, ${symbology[rule_condition]?.color[1]}, ${symbology[rule_condition]?.color[2]})`}} badgeContent=" ">{textField}</Badge>}
            {!symbology[rule_condition]?.color && textField}
          </TableCell>
        </TableRow>)
      }

      //
      let tree = <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {ancesters.map((field) => { 
                return <TableCell>{field.title}</TableCell>
              })}
              <TableCell>Symbology</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tablebody}
          </TableBody>
        </Table>
      </TableContainer>;
      trees.push(tree);
      trees.push(<br/>);
    }

    return <>{trees}</>
  }

  return (
    <Container sx={{ 
      mt: '20px',
      color: 'white',
      position: 'relative',
      '& input, & select, & textarea': {
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        '&::placeholder': { color: 'rgba(255, 255, 255, 0.5)' }
      }
    }}>
      {viewImportLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: 'rgba(0, 0, 0, 0.65)',
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="h6">View import ongoing…</Typography>
        </Box>
      )}
      {hasAnnotations ? (
        /* Alert if annotations exist - show only error message */
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Cannot edit view: annotations exist for this view.
          </Typography>
        </Alert>
      ) : (
        /* Full editing interface - only render if no annotations exist */
        <>
      <Grid container spacing={3} direction="column">

        {/* Information section */}
        <Grid item>
          <Accordion defaultExpanded={true} sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Règles importantes
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Priorité des couleurs :</strong> L'ordre des champs (keys) définit la priorité des couleurs. 
                  Plus un champ est bas dans l'ordre de création (index élevé), plus sa couleur est prioritaire. 
                  En cas de multicouleur pour une annotation, la couleur avec l'index le plus haut sera celle affichée.
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Ordre des champs :</strong> Vous pouvez réorganiser l'ordre des champs par drag & drop dans la liste "List of fields". 
                  Cet ordre détermine la priorité d'affichage des couleurs.
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Export DXF et symbologie :</strong> La symbologie (couleurs, hatch, symbol) définie pour les options des champs détermine 
                  le système de layers de l'export DXF. Chaque combinaison unique de symbologie crée un layer distinct dans le fichier DXF. 
                  Les annotations avec la même symbologie seront regroupées dans le même layer. L'ordre des champs influence également 
                  la hiérarchie des layers dans l'export.
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Dépendances :</strong> Les champs dépendants (child fields) ne s'affichent que lorsque leur option parent est sélectionnée. 
                  Utilisez le bouton "Dependency tree" (raccourci 'T') pour visualiser la hiérarchie complète.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

            <Grid item>
              <Paper sx={{ p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Load template</TableCell>
                        <TableCell align="center">Download template</TableCell>
                        {selectedViewId && (
                          <TableCell align="center">Update view</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button 
                              variant="outlined" 
                              size="small"
                              startIcon={<FileUploadIcon />}
                              onClick={() => setViewSelectorModalOpen(true)}
                            >
                              From view
                            </Button>
                            <Button 
                              variant="outlined" 
                              size="small"
                              startIcon={<FolderOpenIcon />}
                              onClick={open_template_file}
                            >
                              From file
                            </Button>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Button 
                            variant="contained" 
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={save_template_file}
                          >
                            Download template
                          </Button>
                        </TableCell>
                        {selectedViewId && (
                          <TableCell align="center">
                            <Button 
                              variant="contained" 
                              color="primary" 
                              size="small"
                              startIcon={<UpdateIcon />}
                              onClick={updateView}
                            >
                              Update view
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
        </Grid>

            {/* List of fields */}
            <Grid item>
              <Paper id="field_creation" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>List of fields</Typography>
            {fields.length > 0 ? (
              <Box 
                component="ul"
                sx={{ mb: 3, p: '8px', border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.02)' }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Do nothing if drop is on container and not on an element
                }}
              >
                {fields.map((key, idx) =>
                  <Box
                    component="li"
                    key={key.name}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                    sx={{ p: 1, mb: 0.5, cursor: 'grab', borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.05)', border: '1px solid', borderColor: 'divider' }}
                  >
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <Typography variant="body1" sx={{ color: 'text.primary', flex: 1, minWidth: '150px' }}>
                        {key.title}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: '120px' }}>
                        <Select
                          value={key.type}
                          onChange={(e) => {
                            const newFields = fields.map(f => 
                              f.name === key.name ? { ...f, type: e.target.value } : f
                            );
                            setFields(newFields);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ '& .MuiSelect-select': { color: 'white', p: '4px 8px' } }}
                        >
                          <MenuItem value="select">select</MenuItem>
                          <MenuItem value="number">number</MenuItem>
                          <MenuItem value="textarea">textarea</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic' }}>
                No fields yet. Add your first field below.
              </Typography>
            )}
          <Stack direction="row" spacing={2}>
              <TextField 
                id="new_field_title" 
                label="Field title"
                fullWidth
                value={newFieldTitle}
                onChange={(e) => setNewFieldTitle(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addField();
                  }
                }}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select 
                  id="new_field_type" 
                  value={getFormValue("new_field_type", "select")} 
                  onChange={(el) => {setFormValue("new_field_type", el.target.value)}}
                  label="Type"
                >
                <MenuItem value="select">select</MenuItem>
                <MenuItem value="number">number</MenuItem>
                <MenuItem value="textarea">textarea</MenuItem>
              </Select>
            </FormControl>
              <Button variant="contained" onClick={() => {addField()}}>
                Add Field
            </Button>
          </Stack>
          </Paper>
        </Grid>

            {fields.map((field, field_index) => (
              <Grid item key={field.name}>
                {renderField(field, field_index)}
              </Grid>
            ))}

            <Grid item>
              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h4">Auto-generated View JSON</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    multiline
                    fullWidth
                    rows={15}
                    value={JSON.stringify({
          "spec": {
            "description": make_spec(),
            "sidebarConfig": {
              "categoryKey": "category",
              "levelKey": null,
              "titleKey": "title"
            }
          }
                    }, null, 2)}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                  />
                </AccordionDetails>
              </Accordion>
        </Grid>

      </Grid>

          {/* Floating button and drawer */}
          <Tooltip title="View dependency tree (T)" arrow>
            <Fab
              color="primary"
              aria-label="View dependency tree"
              sx={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1000 }}
              onClick={() => setDependencyTreeDrawerOpen(true)}
            >
              <AccountTreeIcon />
            </Fab>
          </Tooltip>

          {/* Drawer to display dependency tree */}
          <Drawer
        variant="persistent"
        anchor="right"
        open={dependencyTreeDrawerOpen}
        onClose={() => setDependencyTreeDrawerOpen(false)}
        PaperProps={{ sx: { width: 400, p: 3, overflowY: 'auto'} }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Dependency Tree</Typography>
          <IconButton onClick={() => setDependencyTreeDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Button variant="contained" fullWidth onClick={() => scrollToElement('field_creation')} sx={{ mb: 2 }}>
          ➕ Add new field
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Structure des dépendances</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={showOptionsInDependencyTree}
              onChange={(e) => setShowOptionsInDependencyTree(e.target.checked)}
              size="small"
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>Afficher les options</Typography>
          </Box>
        </Box>
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
          {renderTree(dependencyTree)}
        </Box>
      </Drawer>
        </>
      )}
      
      {/* View Selector Modal */}
      <ViewSelectorModal
        open={viewSelectorModalOpen}
        onClose={() => setViewSelectorModalOpen(false)}
        defaultInspectionId={inspectionIdFromUrl}
        defaultViewId={viewIdFromUrl}
        onSelectView={importViewAsTemplate}
      />

      {/* Hatch Selector Modal */}
      <SymbologySelectorModal open={hatchModalOpen} onClose={() => setHatchModalOpen(false)}
        onSelect={(fileName) => {
          if (currentHatchField) {
            // fileName will be null if "None" is selected
            updateOptionHatch(currentHatchField.fieldName, currentHatchField.optionText, fileName || null);
          }
        }}
        fileNames={fillPatternFiles}
        type="fill_pattern"
        title="Select fill pattern"
      />

      {/* Symbol Selector Modal */}
      <SymbologySelectorModal open={symbolModalOpen} onClose={() => setSymbolModalOpen(false)}
        onSelect={(fileName) => {
          if (currentSymbolField) {
            // fileName will be null if "None" is selected
            updateOptionSymbol(currentSymbolField.fieldName, currentSymbolField.optionText, fileName || null);
          }
        }}
        fileNames={symbolFiles}
        type="symbols"
        title="Select symbol"
      />
    </Container>
  );
}

export default ViewEditorPage